import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, Typography } from "@mui/material";
import { useUpdateVerificationsMutation } from "Api/Mutations/Verifications/useUpdateVerificationsMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import {
  type UserVerificationUpdate,
  verificationValidationSchema,
} from "Components/UserAccount/Shared/VerificationForm/schema";
import { PARTY_TYPE_TO_VERIFICATIONS_FORM_FIELDS } from "Components/UserAccount/Shared/VerificationForm/utils";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import type {
  PartyType,
  UserVerificationUpdateDto,
} from "Infrastructure/Api/Api";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Components.Account;

type VerificationFormProps = {
  verification: UserVerificationUpdate;
  partyType: PartyType;
};

export const VerificationForm = ({
  verification,
  partyType,
}: VerificationFormProps) => {
  const { t } = useResource();
  const userRole = useCurrentUserRole();
  const {
    handleSubmit,
    control,
    watch,
    reset,
    formState: { isDirty },
  } = useForm<UserVerificationUpdate>({
    resolver: zodResolver(verificationValidationSchema),
    defaultValues: verification,
  });

  const { mutate: updateVerifications, isPending } =
    useUpdateVerificationsMutation({
      onSuccess: (data: UserVerificationUpdateDto) => {
        reset({
          ...data,
          requestedUserID: data.requestedUserID ?? undefined,
          uboUnknown: data.uboUnknown ?? false,
        });
        toast.success(t(COMPONENT_RESOURCES.VerificationSaved));
      },
      onError: () => toast.error(t(COMPONENT_RESOURCES.VerificationError)),
    });

  if (!userRole) {
    return (
      <Typography>{t(COMPONENT_RESOURCES.NoRoleAssignedError)}</Typography>
    );
  }

  const onSubmit = (data: UserVerificationUpdate) => {
    updateVerifications(data);
  };

  const VerificationFormFields =
    PARTY_TYPE_TO_VERIFICATIONS_FORM_FIELDS[partyType];

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={3}>
      <VerificationFormFields control={control} watch={watch} />
      <PrimaryButton
        fullWidth
        type="submit"
        disabled={!isDirty}
        isLoading={isPending}
        variant="contained"
        color="primary"
      >
        {t(Resources.Common.Save)}
      </PrimaryButton>
    </Stack>
  );
};
